import React, { useState, Fragment } from 'react'
import PropTypes from 'prop-types'
import {
  Box,
  Badge,
  Input,
  Select,
  Button,
  Flex,
  Alert,
  InputGroup,
  InputRightElement,
  Icon,
  Stack,
  Container
} from '@chakra-ui/react'
import { SearchOutlined } from '@ant-design/icons';
import truncate from 'lodash/truncate'
import { withTranslation } from 'i18n'
import keys from 'lodash/keys'
import filter from 'lodash/filter'
import includes from 'lodash/includes'
import lowerCase from 'lodash/lowerCase'
import uniq from 'lodash/uniq'
import { humanize, dateFormatLocale } from 'lib/date'
import Pagination from 'components/pagination'
import { loadImage } from 'integrations/unsplash'
import sortArray from 'lib/sort-array'
import HTMLWrapper from 'lib/html-wrapper'

const ProjectLayoutOne = ({ t, meta, theme, projects, tags, i18n, dataTestId }) => {
  const [projectsList, setProjects] = useState(projects)
  const [currentPage, setCurrentPage] = useState(1)
  const [queryParams, setQueryParams] = useState({})
  const [noContent, setNoContent] = useState(false)

  const filterData = (queryParams) => {
    if (!projects) return

    keys(queryParams).map(key => {
      projects = filter(projects, project =>
        includes(lowerCase(project[key]), lowerCase(queryParams[key]))
      )
    })

    return projects
  }

  const query = params => {
    const filterParams = { ...queryParams, ...params }
    const updatedProjectList = filterData(filterParams)

    setProjects(updatedProjectList)
    setNoContent(updatedProjectList.length < 1)
    setQueryParams(filterParams)
  }

  const titleNode = (title) => {
    if (!meta.showProjectName) return false

    return (
      <Box
        color={theme.textColor}
        fontSize='heading'
        fontWeight='heading'
        fontFamily='heading'
        mb={4}
      >
        {truncate(title, { length: 70 })}
      </Box>
    )
  }

  const searchNode = () => {
    if (!meta.showSearch) return false

    return (
      <Box w={['100%', '30%', '30%', '30%']} mr={['0px', '10px', '10px', '10px']} mb={2}>
        <InputGroup>
          <Input
            placeholder={t('editor:editor.projectFinder.filter.searchProjects')}
            onChange={e => query({ name: e.target.value })}
            value={queryParams.name}
            bg="white"
          />
          <InputRightElement children={<Icon as={SearchOutlined} />} />
        </InputGroup>
      </Box>
    )
  }

  const getProjectTags = () => {
    if (!tags) return

    const tagsList = []

    tags.map(tag => {
      tagsList.push(tag['name'])
    })

    return tagsList
  }

  const tagsNode = () => {
    if (!meta.showTagsFilter) return false

    const projectTags = getProjectTags()
    const projectTagsSorted = sortArray(projectTags)

    return (
      <Box w={['100%', '20%', '20%', '20%']} mr={['0px', '10px', '10px', '10px']} mb={2}>
        <Select
          value={queryParams.platformAnalyticsTagList}
          onChange={e => query({ platformAnalyticsTagList: e.target.value })}
          bg="white"
        >
          <option value=''>
            {t('editor:editor.projectFinder.filter.allTags')}
          </option>
          {projectTagsSorted.map(tag => (
            <option
              key={`tag-${tag}`}
              value={tag}
            >
              {tag}
            </option>
          ))}
        </Select>
      </Box>
    )
  }

  const getProjectDisplayYears = (projects, attribute) => {
    if (!projects) return

    const years = []

    projects.map(project => {
      if (project[attribute]) {
        years.push(humanize(project[attribute], 'YYYY'))
      }
    })

    return uniq(years)
  }

  const yearNode = () => {
    if (!meta.showYearFilter) return false

    const displayYears = getProjectDisplayYears(projects, 'publishedAt')

    return (
      <Box w={['100%', '15%', '15%', '15%']} mr={['0px', '10px', '10px', '10px']} mb={2}>
        <Select
          value={queryParams.publishedAt}
          onChange={e => query({ publishedAt: e.target.value })}
          bg="white"
        >
          <option value=''>
            {t('editor:editor.projectFinder.filter.allYears')}
          </option>
          {displayYears.map(year => (
            <option
              key={`yearFilter-${year}`}
              value={year}
            >
              {year}
            </option>
          ))}
        </Select>
      </Box>
    )
  }

  const stateNode = () => {
    if (!meta.showStateFilter) return false

    return (
      <Box w={['100%', '20%', '20%', '20%']} mr={['0px', '10px', '10px', '10px']} mb={2}>
        <Select
          value={queryParams.state}
          onChange={e => query({ state: e.target.value })}
          bg="white"
        >
          <option value=''>
            {t('editor:editor.projectFinder.filter.allStatus')}
          </option>
          <option
            value='published'
          >
            {t('editor:editor.projectFinder.filter.published')}
          </option>
          <option
            value='archived'
          >
            {t('editor:editor.projectFinder.filter.archived')}
          </option>
        </Select>
      </Box>
    )
  }

  const resetFilterButtonNode = () => {
    if (!meta.showResetFilterButton) return false

    const initialQueryParams = {
      name: '',
      state: '',
      platformAnalyticsTagList: '',
      publishedAt: ''
    }

    return (
      <Button
        onClick={() => query(initialQueryParams)}
        bg={theme.ctaBackgroundColor || 'brand'}
        color={theme.ctaTextColor || 'white'}
        _hover={{
          bg: theme.ctaBackgroundHoverColor || theme.ctaBackgroundColor || 'brand',
          color: theme.ctaTextHoverColor || theme.ctaTextColor || '#fff'
        }}
        fontFamily='body'
        fontWeight='body'
        fontSize='14px'
        w={['100%', '15%', '15%', '15%']}
        mb={2}
      >
        {t('editor:editor.projectFinder.filter.resetFilter')}
      </Button>
    )
  }

  const statusNode = (status) => {
    if (!meta.showProjectStatus) return false

    return (
      <Badge
        fontFamily='body'
        fontSize='xs'
        rounded='site'
        variant='solid'
        fontWeight='normal'
        position='absolute'
        top={4}
        right={4}
        backgroundColor='gray.700'
      >
        {status}
      </Badge>
    )
  }

  const CTAButtonNode = (permalink) => {
    if (!meta.showViewProjectButton) return false

    return (
      <Box
        alignItems='center'
        bg={theme.ctaBackgroundColor || 'brand'}
        boxShadow='site'
        color={theme.ctaTextColor || 'white'}
        _hover={{
          bg: theme.ctaBackgroundHoverColor || theme.ctaBackgroundColor || 'brand',
          color: theme.ctaTextHoverColor || theme.ctaTextColor || '#fff'
        }}
        d='inline-flex'
        fontFamily='body'
        fontSize='body'
        fontWeight='normal'
        href={`/${permalink}`}
        justifyContent='center'
        px={2}
        py={1}
        rounded='site'
        textDecoration='none'
        mt={4}
        className='project-finder-button'
      >
        <Box>{t('editor:editor.viewProject')}</Box>
      </Box>
    )
  }

  const publishDateNode = (date) => {
    if (!meta.showProjectTimestamp || !date) return false

    return (
      <Box
        color='gray.700'
        fontFamily='body'
        fontSize='body'
        fontWeight='body'
      >
        {dateFormatLocale(date, i18n.language)}
      </Box>
    )
  }

  const sectionTitleNode = () => {
    if (!meta.title) return false

    return (
      <Box
        as='h2'
        color={theme.sectionTitleColor}
        fontSize='sectionTitle'
        fontWeight='heading'
        fontFamily='heading'
        mb={4}
      >
        {meta.title}
      </Box>
    )
  }

  const descriptionNode = (description) => {
    if (!meta.showProjectDescription) return false

    return (
      <Box
        color={theme.textColor}
        fontFamily='body'
        fontWeight='body'
        fontSize='body'
        wordBreak='break-word'
      >
        <HTMLWrapper html={description} allowTags={['br', '&', 'p']} truncateLength={100} />
      </Box>
    )
  }

  const renderProjects = () => {
    if (noContent) {
      return (
        <Alert status='info'>
          {t('editor:editor.projectFinder.filter.noContent')}
        </Alert>
      )
    }

    const projectsPerPage = meta.projectsPerPage
    const projectPaginationView = meta.projectPaginationView

    let projects = projectsList
    projects = filter(projects, project =>
      project.state === 'published' || project.state === 'archived'
    )

    const totalProjects = projects.length
    const paginate = currentPage => setCurrentPage(currentPage)

    if (projectPaginationView) {
      // get index of first & last project and update the projects array
      const indexOfLastProject = currentPage * projectsPerPage
      const indexOfFirstProject = indexOfLastProject - projectsPerPage

      // If filtered project is greater than projects per page then only splice the projects array
      if (projectsPerPage < projects.length) {
        projects = projects.slice(indexOfFirstProject, indexOfLastProject)
      }
    }

    return (
      <Fragment>
        <Flex
          flexWrap='wrap'
          flexDirection={{xs: 'column', md: 'row' }}
          mx={-6}
        >
          {projects.map((project, index) => {
            return (
              <Box
                key={index}
                px={6}
                py={6}
                w={['100%', '50%', '50%', '50%']}
              >
                <Box
                  as={'a'}
                  bg={theme.cardBackgroundColor || '#ffffff'}
                  boxShadow='site'
                  d='block'
                  href={`/${project.permalink}`}
                  aria-label={`${i18n.t('editor:editor.viewProject')} - ${project.name}`}
                  overflow='hidden'
                  rounded='site'
                  h='full'
                >
                  <Box
                    display='flex'
                    flexDir={[
                      'column-reverse',
                      'column-reverse',
                      'column-reverse',
                      'row'
                    ]}
                    h='full'
                  >
                    <Box
                      display='flex'
                      flexDirection='column'
                      justifyContent='space-between'
                      p={6}
                      w={['100%', '100%', '100%', '60%']}
                      h='full'
                    >
                      <Box>
                        <Stack spacing={3}>
                          <Box>{publishDateNode(project.publishedAt)}</Box>
                          <Box>
                            {titleNode(project.name)}
                          </Box>
                          <Box>{descriptionNode(project.description)}</Box>
                        </Stack>
                      </Box>
                      <Box>
                        {CTAButtonNode(project.permalink)}
                      </Box>
                    </Box>
                    <Box
                      w={['100%', '100%', '100%', '40%']}
                    >
                      <Box
                        backgroundColor='brand'
                        backgroundImage={`url(${loadImage(project.image || project.imageUrl || project.bannerUrl, theme.imageSize || 800)})`}
                        backgroundPosition='center'
                        backgroundRepeat='no-repeat'
                        backgroundSize='cover'
                        h={[48, 48, 48, 'full']}
                        position='relative'
                        role='img'
                        aria-label={!project.imageDescription ? '' : project.imageDescription}
                      >
                        {statusNode(t(`common:common.${project.state}`))}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            )
          })}
        </Flex>
        {(projectsPerPage && totalProjects) > 0 && projectPaginationView &&
          <Pagination
            itemsPerPage={projectsPerPage}
            totalItems={totalProjects}
            paginate={paginate}
            color={theme.ctaTextColor}
            borderColor={theme.ctaBackgroundColor}
          />
        }
      </Fragment>
    )
  }

  return (
    <Box
      bg={theme.backgroundColor}
      px={[2, 4, 8, 16]}
      className='project-finder'
      data-testid={dataTestId}
    >
      <Box bg={theme.backgroundColor} py={8} px={[4, 4, 0, 0]}>
        <Container maxW='container.lg' mx='auto' px={0}>
          {sectionTitleNode()}
          <Flex
            align='center'
            flexDirection={[
              'column',
              'row',
              'row',
              'row'
            ]}
          >
            {searchNode()}
            {tagsNode()}
            {yearNode()}
            {stateNode()}
            {resetFilterButtonNode()}
          </Flex>
          {renderProjects()}
        </Container>
      </Box>
    </Box>
  )
}

ProjectLayoutOne.propTypes = {
  meta: PropTypes.object,
  theme: PropTypes.object,
  t: PropTypes.func.isRequired,
  i18n: PropTypes.object.isRequired,
  dataTestId: PropTypes.string.isRequired
}

export default withTranslation(['editor', 'common'])(ProjectLayoutOne)
